<template>
  <div class="cap-toolbar">
    <!-- Nav arrows -->
    <button class="cap-tb-arr" @click="$emit('navigate', -1)">
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        fill="none"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <button class="cap-tb-arr" @click="$emit('navigate', 1)">
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        fill="none"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>

    <!-- Date range pill -->
    <div class="cap-tb-date" @click="togglePicker" ref="datePill">
      <span>{{ dateRangeLabel }}</span>
      <span class="cap-tb-caret">▾</span>

      <!-- Day / Week / WorkWeek picker -->
      <div
        v-if="pickerOpen && currentView !== 'TimelineMonth'"
        class="cap-dp-popup"
        @click.stop
      >
        <div class="cap-dp-hdr">
          <div class="cap-dp-mon-nav">
            <button class="cap-dp-nbtn" @click="picPrevMonth">&#8249;</button>
            <span class="cap-dp-mon-lbl">{{ monthNames[pickerMonth] }}</span>
            <button class="cap-dp-nbtn" @click="picNextMonth">&#8250;</button>
          </div>
          <div class="cap-dp-yr-nav">
            <button class="cap-dp-nbtn" @click="picPrevYear">&#8249;</button>
            <span class="cap-dp-yr-lbl">{{ pickerYear }}</span>
            <button class="cap-dp-nbtn" @click="picNextYear">&#8250;</button>
          </div>
        </div>
        <div class="cap-dp-grid-wrap">
          <div class="cap-dp-dow-row">
            <div
              v-for="d in ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']"
              :key="d"
              class="cap-dp-dow-cell"
            >
              {{ d }}
            </div>
          </div>
          <div class="cap-dp-day-grid">
            <div
              v-for="cell in calendarCells"
              :key="cell.key"
              class="cap-dp-day"
              :class="cell.classes"
              @click="selectDay(cell.date)"
            >
              {{ cell.date.getDate() }}
            </div>
          </div>
        </div>
        <div class="cap-dp-today-row">
          <span class="cap-dp-today-lnk" @click="selectToday">TODAY</span>
        </div>
      </div>

      <!-- Month picker -->
      <div
        v-if="pickerOpen && currentView === 'TimelineMonth'"
        class="cap-mp-popup"
        @click.stop
      >
        <div class="cap-mp-hdr">
          <button class="cap-dp-nbtn" @click="picPrevYear">&#8249;</button>
          <span class="cap-mp-yr">{{ pickerYear }}</span>
          <button class="cap-dp-nbtn" @click="picNextYear">&#8250;</button>
        </div>
        <div class="cap-mp-grid">
          <div
            v-for="(name, idx) in monthNames"
            :key="idx"
            class="cap-mp-mon"
            :class="{
              sel:
                idx === currentDate.getMonth() &&
                pickerYear === currentDate.getFullYear(),
            }"
            @click="selectMonth(idx)"
          >
            {{ name }}
          </div>
        </div>
        <div class="cap-dp-today-row">
          <span class="cap-dp-today-lnk" @click="selectToday">TODAY</span>
        </div>
      </div>
    </div>

    <!-- Spacer to push view buttons to the right -->
    <div style="flex: 1"></div>

    <!-- Summary/Detailed toggle group -->
    <div class="cap-tb-toggle">
      <button
        class="cap-tb-toggle-btn"
        :class="{ active: activeMode === 'summary' }"
        @click="toggleMode('summary')"
      >
        Summary
      </button>
      <button
        class="cap-tb-toggle-btn"
        :class="{ active: activeMode === 'detailed' }"
        @click="toggleMode('detailed')"
      >
        Detailed
      </button>
    </div>

    <!-- Divider -->
    <div class="cap-tb-divider"></div>

    <!-- View buttons group -->
    <div class="cap-tb-views">
      <button class="cap-tb-view" @click="$emit('go-today')">TODAY</button>
      <button
        class="cap-tb-view"
        :class="{ active: currentView === 'TimelineDay' }"
        @click="$emit('set-view', 'TimelineDay')"
      >
        DAY
      </button>
      <button
        class="cap-tb-view"
        :class="{ active: currentView === 'TimelineWeek' }"
        @click="$emit('set-view', 'TimelineWeek')"
      >
        WEEK
      </button>
      <button
        class="cap-tb-view"
        :class="{ active: currentView === 'TimelineWorkWeek' }"
        @click="$emit('set-view', 'TimelineWorkWeek')"
      >
        WORK WEEK
      </button>
      <button
        class="cap-tb-view"
        :class="{ active: currentView === 'TimelineMonth' }"
        @click="$emit('set-view', 'TimelineMonth')"
      >
        MONTH
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "CapacityToolbar",

  props: {
    currentDate: { type: Date, required: true },
    currentView: { type: String, required: true },
  },

  emits: ["navigate", "set-view", "go-today", "date-selected", "toggle-mode"],

  data() {
    return {
      pickerOpen: false,
      pickerMonth: new Date().getMonth(),
      pickerYear: new Date().getFullYear(),
      monthNames: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      monthsFull: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      activeMode: "detailed",
    };
  },

  computed: {
    dateRangeLabel() {
      const d = this.currentDate;
      const fmt = (dt) =>
        `${String(dt.getDate()).padStart(2, "0")}-${
          this.monthNames[dt.getMonth()]
        }-${dt.getFullYear()}`;

      if (this.currentView === "TimelineDay") {
        return fmt(d);
      }
      if (this.currentView === "TimelineWeek") {
        const sun = this.sundayOf(d);
        const sat = new Date(sun);
        sat.setDate(sat.getDate() + 6);
        return `${fmt(sun)} - ${fmt(sat)}`;
      }
      if (this.currentView === "TimelineWorkWeek") {
        const mon = this.mondayOf(d);
        const fri = new Date(mon);
        fri.setDate(fri.getDate() + 4);
        return `${fmt(mon)} - ${fmt(fri)}`;
      }
      if (this.currentView === "TimelineMonth") {
        return `${this.monthsFull[d.getMonth()]} ${d.getFullYear()}`;
      }
      return fmt(d);
    },

    calendarCells() {
      const year = this.pickerYear;
      const month = this.pickerMonth;
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMon = new Date(year, month + 1, 0).getDate();
      const prevDays = new Date(year, month, 0).getDate();
      const cells = [];

      // Previous month padding
      for (let i = firstDay - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevDays - i);
        cells.push({ key: `p${i}`, date, classes: ["other"] });
      }

      // Current month
      const today = new Date();
      for (let d = 1; d <= daysInMon; d++) {
        const date = new Date(year, month, d);
        const classes = [];
        if (this.isSameDay(date, today)) classes.push("today");

        if (this.currentView === "TimelineDay") {
          if (this.isSameDay(date, this.currentDate)) classes.push("sel");
        } else if (this.currentView === "TimelineWeek") {
          const sun = this.sundayOf(this.currentDate);
          const sat = new Date(sun);
          sat.setDate(sat.getDate() + 6);
          if (this.isSameDay(date, sun)) classes.push("rng-s");
          else if (this.isSameDay(date, sat)) classes.push("rng-e");
          else if (date > sun && date < sat) classes.push("in-rng");
        } else if (this.currentView === "TimelineWorkWeek") {
          const mon = this.mondayOf(this.currentDate);
          const fri = new Date(mon);
          fri.setDate(fri.getDate() + 4);
          if (this.isSameDay(date, mon)) classes.push("rng-s");
          else if (this.isSameDay(date, fri)) classes.push("rng-e");
          else if (date > mon && date < fri) classes.push("in-rng");
        }

        cells.push({ key: `c${d}`, date, classes });
      }

      // Next month padding to fill 6 rows
      const remainder = 42 - cells.length;
      for (let n = 1; n <= remainder; n++) {
        cells.push({
          key: `n${n}`,
          date: new Date(year, month + 1, n),
          classes: ["other"],
        });
      }
      return cells;
    },
  },

  mounted() {
    document.addEventListener("click", this.closePicker);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closePicker);
  },

  methods: {
    togglePicker() {
      this.pickerOpen = !this.pickerOpen;
      if (this.pickerOpen) {
        this.pickerMonth = this.currentDate.getMonth();
        this.pickerYear = this.currentDate.getFullYear();
      }
    },
    closePicker(e) {
      if (this.$refs.datePill && !this.$refs.datePill.contains(e.target)) {
        this.pickerOpen = false;
      }
    },
    picPrevMonth() {
      if (this.pickerMonth === 0) {
        this.pickerMonth = 11;
        this.pickerYear--;
      } else {
        this.pickerMonth--;
      }
    },
    picNextMonth() {
      if (this.pickerMonth === 11) {
        this.pickerMonth = 0;
        this.pickerYear++;
      } else {
        this.pickerMonth++;
      }
    },
    picPrevYear() {
      this.pickerYear--;
    },
    picNextYear() {
      this.pickerYear++;
    },

    selectDay(date) {
      this.pickerOpen = false;
      this.$emit("date-selected", new Date(date));
    },
    selectMonth(monthIdx) {
      this.pickerOpen = false;
      this.$emit("date-selected", new Date(this.pickerYear, monthIdx, 1));
    },
    selectToday() {
      this.pickerOpen = false;
      this.$emit("go-today");
    },

    isSameDay(a, b) {
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },
    mondayOf(date) {
      const d = new Date(date);
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      return d;
    },
    sundayOf(date) {
      const d = new Date(date);
      d.setDate(d.getDate() - d.getDay());
      return d;
    },

    toggleMode(mode) {
      this.activeMode = mode;
      this.$emit("toggle-mode", mode);
    },
  },
};
</script>

<style scoped lang="scss">
.cap-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 46px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.cap-tb-arr {
  width: 26px;
  height: 26px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  &:hover {
    background: #f9fafb;
  }
}

.cap-tb-date {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  border-radius: 3px;
  position: relative;
  &:hover {
    background: #f9fafb;
  }
}
.cap-tb-caret {
  color: #9ca3af;
  font-size: 10px;
  margin-left: 3px;
}

.cap-dp-popup {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  z-index: 300;
  min-width: 270px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
.cap-dp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.cap-dp-mon-nav,
.cap-dp-yr-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cap-dp-mon-lbl {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  min-width: 90px;
}
.cap-dp-yr-lbl {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  min-width: 36px;
  text-align: center;
}
.cap-dp-nbtn {
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  border-radius: 2px;
  &:hover {
    background: #f3f4f6;
  }
}
.cap-dp-grid-wrap {
  padding: 6px 10px 8px;
}
.cap-dp-dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 2px;
}
.cap-dp-dow-cell {
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  padding: 2px 0;
}
.cap-dp-day-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.cap-dp-day {
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  border-radius: 50%;
  width: 30px;
  margin: 1px auto;
  &:hover {
    background: #e0f2fe;
  }
  &.other {
    color: #d1d5db;
  }
  &.today {
    background: #dbeafe;
    color: #2563eb;
    font-weight: 700;
  }
  &.sel {
    background: #2a95c5;
    color: #fff;
    border-radius: 50%;
  }
  &.in-rng {
    background: #e0f2fe;
    border-radius: 0;
    width: 100%;
  }
  &.rng-s {
    background: #2a95c5;
    color: #fff;
    border-radius: 50% 0 0 50%;
    width: 100%;
  }
  &.rng-e {
    background: #2a95c5;
    color: #fff;
    border-radius: 0 50% 50% 0;
    width: 100%;
  }
}
.cap-dp-today-row {
  text-align: center;
  padding: 5px 12px 7px;
  border-top: 1px solid #f3f4f6;
}
.cap-dp-today-lnk {
  font-size: 12px;
  color: #d92d20;
  font-weight: 700;
  cursor: pointer;
}

.cap-mp-popup {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  z-index: 300;
  width: 220px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
.cap-mp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
}
.cap-mp-yr {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.cap-mp-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 8px;
}
.cap-mp-mon {
  padding: 8px 4px;
  text-align: center;
  font-size: 13px;
  color: #111827;
  cursor: pointer;
  border-radius: 3px;
  &:hover {
    background: #e0f2fe;
  }
  &.sel {
    background: #2a95c5;
    color: #fff;
  }
}

.cap-tb-toggle {
  display: flex;
  // gap: 4px;
}

.cap-tb-toggle-btn:first-child {
  border-radius: 3px 0px 0px 3px;
}
.cap-tb-toggle-btn:last-child {
  border-radius: 0px 3px 3px 0px;
}

.cap-tb-toggle-btn {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  background: #fff;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  font-family: "Roboto", sans-serif;
  transition: all 0.2s ease;
  &:hover {
    color: #111827;
    border-color: #bfdbfe;
  }
  &.active {
    color: #2a95c5;
    border-color: #2a95c5;
    background: #fff;
    font-weight: 500;
  }
}

.cap-tb-divider {
  width: 1px;
  height: 20px;
  background-color: #e5e7eb;
  margin: 0 8px;
}

.cap-tb-views {
  display: flex;
  gap: 2px;
}
.cap-tb-view {
  padding: 5px 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  font-family: "Roboto", sans-serif;
  &:hover {
    color: #111827;
  }
  &.active {
    color: #2a95c5;
    font-weight: 500;
    background: transparent;
  }
}
</style>
